/**
 * TimelineTaskDragHandlesLayer Component
 *
 * Renders interactive drag handles for task editing.
 * Each task gets TWO handles: start (left edge) and end (right edge).
 * Positioned in bottom half when viewStrategy='both'.
 *
 * Unlike phases (which are continuous), tasks are independent and isolated.
 * Each handle controls only that specific task, with no cascading effects.
 */

import React from 'react'
import { useTimelineStaticContext } from '../../context/timeline-static-context'
import { PhaseIntersectionHandle } from '../content/phase-intersection-handle'
import {
  EXPANDED_ROW_HEIGHT,
  EXPANDED_ROW_HEIGHT_GAP,
  VERTICAL_DAY_TRACKER_SPACE,
} from '../../timeline-constants'

/**
 * TimelineTaskDragHandlesLayer - Interactive drag handles for tasks
 *
 * Renders TWO handles per task:
 * - Start handle: Left edge of task (adjusts start date, end stays fixed)
 * - End handle: Right edge of task (adjusts duration, start stays fixed)
 *
 * Unlike phases (which cascade), tasks are independent - dragging one task
 * does NOT affect other tasks.
 *
 * @param {Object} props
 * @param {Array} props.crops - Transformed crop data
 * @param {boolean} props.forceAllExpand - Force all crops expanded
 * @param {Object|string} props.draggingIndex - Currently dragging handle: { type, taskIndex }
 * @param {Object|string} props.hoveredIntersection - Currently hovered handle: { type, taskIndex }
 * @param {Function} props.setHoveredIntersection - Hover handler
 * @param {Function} props.handleDragStart - Drag start handler
 */
export const TimelineTaskDragHandlesLayer = React.memo(({
  crops,
  forceAllExpand,
  draggingIndex,
  hoveredIntersection,
  setHoveredIntersection,
  handleDragStart,
  barHeight,  // optional override for bar height (e.g. smaller succession rows)
}) => {
  const { expandedIds, weekWidth, getRowY, viewStrategy, collapsedRowHeight, collapsedRowHeightGap } = useTimelineStaticContext()

  return (
    <>
      {crops.map((crop) => {
        const isExpanded = forceAllExpand || expandedIds.includes(crop.id)
        const rowY = getRowY(crop.id) + VERTICAL_DAY_TRACKER_SPACE * 8

        // Calculate row height: double when showing both phases and tasks
        const baseRowH = isExpanded ? EXPANDED_ROW_HEIGHT : collapsedRowHeight
        const rowH = viewStrategy === 'both' && !crop.isOrgTaskRow ? baseRowH * 2 : baseRowH

        const barH = barHeight ?? (isExpanded
          ? EXPANDED_ROW_HEIGHT - EXPANDED_ROW_HEIGHT_GAP
          : collapsedRowHeight - collapsedRowHeightGap)

        // Position handles on task bars (bottom half when both views shown)
        const subRowH = viewStrategy === 'both' && !crop.isOrgTaskRow ? rowH / 2 : rowH
        const y = viewStrategy === 'both' && !crop.isOrgTaskRow
          ? rowY + subRowH + (subRowH / 2) - (barH / 2)  // Bottom half
          : rowY + (rowH / 2) - (barH / 2)

        // Skip if no tasks
        if (!crop.tasks || crop.tasks.length === 0) {
          return null
        }

        return (
          <g key={`task-handles-${crop.id}`}>
            {/* Render start AND end handles for each interactive (non-readOnly) task */}
            {crop.tasks.map((task, tidx) => {
              if (task.readOnly || task.state === 'completed') return null
              const taskStartX = task.start * weekWidth
              const taskEndX = (task.start + task.len) * weekWidth

              const startHandleId = { type: 'start', taskIndex: tidx }
              const endHandleId = { type: 'end', taskIndex: tidx }

              // Helper to compare handle objects
              const isSameHandle = (handle1, handle2) => {
                if (!!handle1 && !!handle2 && 
                  typeof handle1 === 'object' && typeof handle2 === 'object') {
                  return handle1.type === handle2.type && handle1.taskIndex === handle2.taskIndex
                }
                return handle1 === handle2
              }

              return (
                <g key={`task-${tidx}-handles`}>
                  {/* Start handle (left edge of task) */}
                  <PhaseIntersectionHandle
                    x={taskStartX}
                    y={y}
                    height={barH}
                    intersectionIndex={startHandleId}
                    onDragStart={handleDragStart}
                    isDragging={isSameHandle(draggingIndex, startHandleId)}
                    isHovered={isSameHandle(hoveredIntersection, startHandleId)}
                    onHover={setHoveredIntersection}
                  />

                  {/* End handle (right edge of task) */}
                  <PhaseIntersectionHandle
                    x={taskEndX}
                    y={y}
                    height={barH}
                    intersectionIndex={endHandleId}
                    onDragStart={handleDragStart}
                    isDragging={isSameHandle(draggingIndex, endHandleId)}
                    isHovered={isSameHandle(hoveredIntersection, endHandleId)}
                    onHover={setHoveredIntersection}
                  />
                </g>
              )
            })}
          </g>
        )
      })}
    </>
  )
})
